import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, User, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { caseStatusColor, priorityColor } from "@/lib/caseUtils";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CaseDetailDialog from "@/components/CaseDetailDialog";

export default function MyCases() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("open");
  const [memberFilter, setMemberFilter] = useState("all");
  const [openCaseId, setOpenCaseId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (isAdmin) {
        if (memberFilter !== "all") params.set("tech", memberFilter);
      } else {
        params.set("tech", user.name);
      }
      if (status !== "open" && status !== "all") params.set("status", status);
      const { data } = await api.get(`/cases?${params.toString()}`);
      setRows(status === "open" ? data.filter((c) => c.status === "Open" || c.status === "In Progress") : data);
    } catch (e) { setRows([]); }
  };
  useEffect(() => { load(); }, [status, memberFilter, user]);
  useEffect(() => { if (isAdmin) api.get("/team/members").then((r) => setMembers(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("zeefa:refresh", h);
    return () => window.removeEventListener("zeefa:refresh", h);
  }, [status, memberFilter, user]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2"><Wrench size={24} className="text-primary" /> My Cases</h1>
          <p className="text-muted-foreground mt-1">{isAdmin ? "Every case across the team" : "Cases assigned to you"} — {rows.length} case{rows.length !== 1 && "s"}.</p>
        </div>
        {isAdmin && (
          <div className="w-56">
            <Select value={memberFilter} onValueChange={setMemberFilter}>
              <SelectTrigger className="rounded-sm" data-testid="member-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All team members</SelectItem>
                {members.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}{m.role ? ` · ${m.role}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="open" className="rounded-sm" data-testid="filter-open">Open</TabsTrigger>
          <TabsTrigger value="Resolved" className="rounded-sm" data-testid="filter-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="Closed" className="rounded-sm" data-testid="filter-closed">Closed</TabsTrigger>
          <TabsTrigger value="all" className="rounded-sm" data-testid="filter-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-sm border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Customer</TableHead><TableHead>Issue</TableHead><TableHead>Type</TableHead>
              <TableHead>Priority</TableHead><TableHead>Assigned to</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="tech-cases-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No cases in this view.</TableCell></TableRow>}
            {rows.map((k) => (
              <TableRow key={k.id} className="cursor-pointer" onClick={() => setOpenCaseId(k.id)} data-testid={`case-row-${k.id}`}>
                <TableCell className="font-medium">{k.client_name}</TableCell>
                <TableCell className="text-sm max-w-[220px] truncate" title={k.title}>{k.title}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{k.category}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${priorityColor[k.priority]}`}>{k.priority}</Badge></TableCell>
                <TableCell className="text-sm flex items-center gap-1.5"><User size={13} className="text-muted-foreground" /> {k.tech_assigned || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${caseStatusColor[k.status]}`}>{k.status}</Badge></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <button className="text-muted-foreground hover:text-primary" onClick={() => navigate(`/clients/${k.client_id}`)} title="Open customer" data-testid={`case-goto-${k.id}`}><ExternalLink size={15} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CaseDetailDialog open={!!openCaseId} onOpenChange={(v) => !v && setOpenCaseId(null)} caseId={openCaseId} onChanged={load} />
    </div>
  );
}
